import {isEqual} from 'lodash';

import {ApplicationCredentialsModel} from '../../../models/api/applicationCredentialsModel';
import {
  ReadonlyServerModel,
  ServerAuthenticationConfig,
  ServerAuthenticationStrategiesEnum,
  ServerModel,
  UnsavedServerModel,
  WritableServerModel,
} from '../../../models/api/serverModel';
import {computeServerEditorFormErrors} from '../../tools/servers/serverEditorFormErrors';

interface ReadonlyServerState {
  isWritable: false;
  origin: string;
  authenticationConfig: Pick<ServerAuthenticationConfig, 'strategy'>;
}

interface WritableServerState {
  isWritable: true;
  origin: string;
  authenticationConfig: ServerAuthenticationConfig;
}

export type ServerState = ReadonlyServerState | WritableServerState;

export interface ApplicationCredentialsState {
  credentials: ApplicationCredentialsModel;
}

interface ReadonlyServerEditorState {
  serverState: ReadonlyServerState;
  applicationCredentialsState: ApplicationCredentialsState;
  initialServer?: ReadonlyServerModel;
}

interface WritableServerEditorState {
  serverState: WritableServerState;
  applicationCredentialsState: ApplicationCredentialsState;
  initialServer?: WritableServerModel;
}

export type ServerEditorState = ReadonlyServerEditorState | WritableServerEditorState;

export const DEFAULT_APPLICATION_CREDENTIALS: Record<
  ServerAuthenticationStrategiesEnum,
  ApplicationCredentialsModel
> = {
  [ServerAuthenticationStrategiesEnum.NONE]: {type: ServerAuthenticationStrategiesEnum.NONE},
  [ServerAuthenticationStrategiesEnum.BASIC]: {
    type: ServerAuthenticationStrategiesEnum.BASIC,
    username: '',
    password: '',
    createdAt: Date.now(),
  },
  [ServerAuthenticationStrategiesEnum.BEARER]: {
    type: ServerAuthenticationStrategiesEnum.BEARER,
    access_token: '',
    createdAt: Date.now(),
  },
  [ServerAuthenticationStrategiesEnum.OAUTH2]: {
    type: ServerAuthenticationStrategiesEnum.OAUTH2,
    access_token: '',
    createdAt: Date.now(),
  },
};

export const ServerEditorState = {
  get default(): ServerEditorState {
    return {
      serverState: {
        isWritable: true,
        origin: '',
        authenticationConfig: {
          strategy: ServerAuthenticationStrategiesEnum.NONE,
        },
      },
      applicationCredentialsState: {
        credentials: DEFAULT_APPLICATION_CREDENTIALS[ServerAuthenticationStrategiesEnum.NONE],
      },
    };
  },

  createInitialState(
    initialServer: ServerModel | undefined,
    applicationCredentials: ApplicationCredentialsModel | undefined,
  ): ServerEditorState {
    if (!initialServer) {
      return {...this.default};
    }

    // Only use fetched credentials if they are consistent with the authentication config.
    const credentials =
      applicationCredentials?.type === initialServer.authenticationConfig.strategy
        ? applicationCredentials
        : DEFAULT_APPLICATION_CREDENTIALS[initialServer.authenticationConfig.strategy];

    if (initialServer.isWritable) {
      return {
        ...this.default,
        initialServer,
        serverState: {
          isWritable: true,
          origin: initialServer.origin,
          authenticationConfig: initialServer.authenticationConfig,
        },
        applicationCredentialsState: {
          credentials,
        },
      };
    }

    return {
      ...this.default,
      initialServer,
      serverState: {
        isWritable: false,
        origin: initialServer.origin,
        authenticationConfig: initialServer.authenticationConfig,
      },
      applicationCredentialsState: {
        credentials,
      },
    };
  },

  toServer(state: ServerEditorState): UnsavedServerModel {
    const {serverState} = state;

    return {
      origin: serverState.origin,
      // @ts-expect-error: this could be "readonly" (auth strategy) or "writable" (full auth details).
      // The caller must correctly decide what to do with this information.
      authenticationConfig: serverState.authenticationConfig,
    };
  },

  toApplicationCredentials(state: ServerEditorState): ApplicationCredentialsModel {
    return state.applicationCredentialsState.credentials;
  },

  hasDiff(a: ServerEditorState, b: ServerEditorState) {
    if (a === b) {
      return false;
    }

    const aState = a.serverState;
    const bState = b.serverState;
    const aCredentialsState = a.applicationCredentialsState.credentials;
    const bCredentialsState = b.applicationCredentialsState.credentials;

    if (aState.origin !== bState.origin) {
      return true;
    }

    if (!isEqual(aState.authenticationConfig, bState.authenticationConfig)) {
      return true;
    }

    if (!isEqual(aCredentialsState, bCredentialsState)) {
      return true;
    }

    return false;
  },

  computeFormErrors(state: ServerEditorState) {
    return computeServerEditorFormErrors(state);
  },
};
